import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, FileText, Building2, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Listing } from '@/lib/index';

interface SidebarProps {
  listings: Listing[];
  activeId: string;
  onSelect: (id: string) => void;
  onCreate: () => void;
  onDelete: (id: string) => void;
}

export function Sidebar({ listings, activeId, onSelect, onCreate, onDelete }: SidebarProps) {
  return (
    <aside className="w-64 flex-shrink-0 bg-sidebar border-r border-sidebar-border flex flex-col h-screen sticky top-0">
      {/* Logo Header */}
      <div className="px-5 py-5 border-b border-sidebar-border">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center shadow-sm">
            <Building2 size={18} className="text-primary-foreground" />
          </div>
          <div>
            <p className="font-semibold text-sidebar-foreground text-sm leading-tight">Seller Net Sheet</p>
            <p className="text-xs text-muted-foreground leading-tight">Danielle Edwards</p>
          </div>
        </div>
      </div>

      {/* New Sheet Button */}
      <div className="px-4 pt-4 pb-2">
        <Button
          onClick={onCreate}
          className="w-full gap-2 bg-primary text-primary-foreground hover:opacity-90 text-sm h-9"
        >
          <Plus size={14} />
          New Sheet
        </Button>
      </div>

      {/* Listings */}
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2 mb-2">
          Listings ({listings.length})
        </p>
        <AnimatePresence>
          {listings.map((listing) => (
            <motion.div
              key={listing.id}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -8 }}
              transition={{ duration: 0.15 }}
            >
              <button
                onClick={() => onSelect(listing.id)}
                className={`w-full text-left rounded-lg px-3 py-2.5 flex items-start gap-2.5 group transition-all duration-150 ${
                  activeId === listing.id
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-sidebar-foreground hover:bg-primary/10'
                }`}
              >
                <FileText size={14} className="mt-0.5 flex-shrink-0 opacity-70" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold truncate leading-tight">
                    {listing.sellerName || 'New Listing'}
                  </p>
                  <p className={`text-xs truncate leading-tight mt-0.5 ${
                    activeId === listing.id ? 'text-primary-foreground/70' : 'text-muted-foreground'
                  }`}>
                    {listing.property || 'No address'}
                  </p>
                </div>
                {activeId === listing.id && (
                  <ChevronRight size={12} className="mt-0.5 flex-shrink-0 opacity-70" />
                )}
                {activeId !== listing.id && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onDelete(listing.id); }}
                    className="opacity-0 group-hover:opacity-60 hover:!opacity-100 transition-opacity text-destructive"
                    title="Delete listing"
                  >
                    <Trash2 size={12} />
                  </button>
                )}
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-sidebar-border">
        <p className="text-xs text-muted-foreground text-center">NC REALTORS® Compliant</p>
      </div>
    </aside>
  );
}
